import type { MessageDescriptor } from 'react-intl';
import { Toggle } from '@base-ui/react/toggle';
import { ToggleGroup } from '@base-ui/react/toggle-group';
import clsx from 'clsx';
import { FormattedError } from '../formatted/index.ts';

type BaseProps = React.PropsWithChildren<{
  disabled?: boolean;
  error?: React.ReactNode | MessageDescriptor;
  label?: React.ReactNode;
  onBlur?: () => void;
}>;

type SingleValueProps<TValue extends string> = BaseProps & {
  multiple: false;
  onChange?: (value: TValue) => void;
  value: TValue | null | undefined;
};

type MultipleValueProps<TValue extends string> = BaseProps & {
  multiple: true;
  onChange?: (value: TValue[]) => void;
  value: TValue[] | undefined;
};

export type ChipToggleGroupProps<TValue extends string> =
  | MultipleValueProps<TValue>
  | SingleValueProps<TValue>;

export function ChipToggleGroup<TValue extends string>(props: ChipToggleGroupProps<TValue>) {
  const { children, disabled = false, error, label } = props;

  const errorMessage =
    error && typeof error !== 'boolean' ? <FormattedError error={error} /> : undefined;

  return (
    <fieldset>
      {label && <label className="mb-1.5 block text-sm/6 font-medium text-gray-700">{label}</label>}

      {errorMessage && <p className="mb-1.5 justify-start text-sm text-red-600">{errorMessage}</p>}

      {props.multiple ? (
        <ToggleGroup
          className="flex flex-wrap gap-2"
          disabled={disabled}
          multiple
          onBlur={props.onBlur}
          onValueChange={value => {
            props.onChange?.(value);
          }}
          value={props.value}
        >
          {children}
        </ToggleGroup>
      ) : (
        <ToggleGroup
          className="flex flex-wrap gap-2"
          disabled={disabled}
          multiple={false}
          onBlur={props.onBlur}
          onValueChange={value => {
            props.onChange?.(value[0]);
          }}
          value={props.value ? [props.value] : undefined}
        >
          {children}
        </ToggleGroup>
      )}
    </fieldset>
  );
}

export function ChipToggleGroupOption<TValue extends string>({
  children,
  disabled,
  icon,
  value
}: React.PropsWithChildren<{
  disabled?: boolean;
  icon?: React.ReactNode;
  value: TValue;
}>) {
  return (
    <Toggle
      disabled={disabled}
      value={value}
      render={(props, state) => (
        <button
          {...props}
          className={clsx(
            'relative flex cursor-pointer items-center justify-center rounded-full px-4 py-2 text-sm font-medium text-gray-900 select-none',
            'ring-1 ring-gray-300 ring-inset',
            !state.pressed && 'bg-white hover:bg-gray-50',
            state.pressed && 'bg-primary-50 ring-2 ring-primary-600',
            state.disabled && 'pointer-events-none opacity-25',
            'focus-visible:outline-hidden focus-visible:after:pointer-events-none focus-visible:after:absolute focus-visible:after:inset-0 focus-visible:after:rounded-full focus-visible:after:ring-2 focus-visible:after:ring-primary-500 focus-visible:after:ring-offset-2'
          )}
        >
          {!!icon && <div className="mr-2 -ml-0.5 *:size-4">{icon}</div>}
          {children}
        </button>
      )}
    />
  );
}
